import React, { useEffect, useState } from 'react';
import { invoiceApi } from '@/api/endpoints/invoices';
import { InvoiceResponse } from '@/types/invoice';
import { formatCurrency } from '@/utils/format';

interface DiscountVoucherInvoiceSelectorProps {
  customerId?: string;
  selectedInvoiceCodes: string[];
  onChange: (codes: string[]) => void;
  discountType: 'percent' | 'amount';
  discountValue: number;
}

const DiscountVoucherInvoiceSelector: React.FC<DiscountVoucherInvoiceSelectorProps> = ({
  customerId,
  selectedInvoiceCodes,
  onChange,
  discountType,
  discountValue,
}) => {
  const [availableInvoices, setAvailableInvoices] = useState<InvoiceResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load invoices when customer is selected
  useEffect(() => {
    if (!customerId) {
      setAvailableInvoices([]);
      return;
    }

    const loadInvoices = async () => {
      setIsLoading(true);
      try {
        const response = await invoiceApi.getInvoices(1, 100, {
          customer_id: customerId,
          status: 'waiting_payment',
        });
        setAvailableInvoices(response.data || []);
      } catch (error) {
        setAvailableInvoices([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadInvoices();
  }, [customerId]);

  const handleCheckboxChange = (invoiceCode: string) => {
    if (selectedInvoiceCodes.includes(invoiceCode)) {
      onChange(selectedInvoiceCodes.filter(code => code !== invoiceCode));
    } else {
      onChange([...selectedInvoiceCodes, invoiceCode]);
    }
  };

  const calculateDiscount = (remaining: number): number => {
    if (discountType === 'percent') {
      return (remaining * discountValue) / 100;
    }
    return discountValue;
  };

  const selectedInvoices = availableInvoices.filter(inv => 
    selectedInvoiceCodes.includes(inv.code)
  );

  if (!customerId) {
    return (
      <p className="text-sm text-gray-500">
        Vui lòng chọn khách hàng trước
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Left: Available Invoices Table */}
      <div className="border border-gray-200 rounded-lg">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-900">
            Hóa đơn chưa hoàn thành ({availableInvoices.length})
          </h4>
        </div>
        <div className="overflow-auto max-h-96">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="w-12 px-3 py-2"></th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Mã HĐ
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Order ID
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                  Nợ còn lại
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {availableInvoices.map(invoice => {
                const remaining = (invoice.total || 0) - (invoice.customer_paid || 0);
                const isSelected = selectedInvoiceCodes.includes(invoice.code);
                return (
                  <tr 
                    key={invoice.code}
                    onClick={() => handleCheckboxChange(invoice.code)}
                    className={`cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCheckboxChange(invoice.code)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">
                      {invoice.code}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      {invoice.order_id}
                    </td>
                    <td className="px-3 py-2 text-sm text-right text-gray-900">
                      {formatCurrency(remaining)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right: Selected Invoices with Discount Preview */}
      <div className="border border-gray-200 rounded-lg">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-900">
            Hóa đơn đã chọn ({selectedInvoices.length})
          </h4>
        </div>
        <div className="overflow-auto max-h-96">
          {selectedInvoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              Chưa chọn hóa đơn nào
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Mã HĐ
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Nợ
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Chiết khấu
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Sau CK
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {selectedInvoices.map(invoice => {
                  const remaining = (invoice.total || 0) - (invoice.customer_paid || 0);
                  const discount = calculateDiscount(remaining);
                  const afterDiscount = remaining - discount;
                  return (
                    <tr key={invoice.code} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">
                        {invoice.code}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-gray-900">
                        {formatCurrency(remaining)}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-green-600 font-medium">
                        {formatCurrency(discount)}
                      </td>
                      <td className="px-3 py-2 text-sm text-right text-blue-600 font-medium">
                        {formatCurrency(afterDiscount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscountVoucherInvoiceSelector;
